import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/content";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";

const usePaymentStatus = (sessionId) => {
  const [state, setState] = useState(sessionId ? "checking" : "unknown");
  useEffect(() => {
    if (!sessionId) return undefined;
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") return setState(data.mode === "monthly" ? "paidMonthly" : "paid");
        if (["expired", "failed"].includes(data.payment_status)) return setState("failed");
      } catch {
        return setState("unknown");
      }
      tries += 1;
      if (tries >= 10) return setState("pending");
      timer = setTimeout(poll, 2000);
      return undefined;
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);
  return state;
};

const COPY = {
  checking: ["One moment", "Confirming your gift..."],
  paid: ["Thank you", "Your gift helps another military couple find support. A receipt is on its way to your email."],
  paidMonthly: ["Thank you", "Your monthly gift helps couples all year long. We've emailed you a private link to pause or cancel it whenever you need to."],
  pending: ["Almost there", "Your payment is still processing. You'll receive a receipt by email once it completes."],
  failed: ["Not completed", "Your donation didn't go through. No money was taken."],
  unknown: ["Something went wrong", "We couldn't confirm this donation. Please call us if you need help."],
  cancel: ["No gift made", "You left checkout before giving. You're welcome to try again anytime."],
};

export default function PaymentResult({ cancelled = false }) {
  const [params] = useSearchParams();
  const status = usePaymentStatus(cancelled ? null : params.get("session_id"));
  const [title, body] = COPY[cancelled ? "cancel" : status];

  return (
    <div className="site bg-sage text-pine min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 max-w-3xl mx-auto px-6 py-24 md:py-32" data-testid="payment-result-page">
        <p className="lb text-brass-ink fu">Donation</p>
        <h1 className="dp mt-6" data-testid="payment-result-title">
          <span className="ln"><span><em>{title}.</em></span></span>
        </h1>
        <p className="mt-6 max-w-lg text-moss fu s1" data-testid="payment-result-message">{body}</p>
        <div className="mt-10 flex flex-wrap gap-6 items-center fu s2">
          <Link to="/" className="bg-pine text-sage rounded-full px-7 py-4" data-testid="payment-result-home-button">Back to home</Link>
          {!status.startsWith("paid") && (
            <a href="/#donate" className="underline underline-offset-4 decoration-brass" data-testid="payment-result-retry-link">Try donating again</a>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
